import { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { Redirect } from 'react-router-dom';
import { signUpStatus, profileFields, countryCodes } from '../../constants/constants';
import useQueryServices from '../../hooks/useQueryServices';
import StyledButton from '../general/StyledButton';
import firebase from '../../config/fbConfig';
import { generateProfileArray, sortByState } from './helpers/profileHelpers';
import CircleLoader from '../loaders/CircleLoader';
import XLSX from 'xlsx';
import { downloadFile, serialFileToArrayBuffer, getArraybyRegion } from './helpers/profileHelpers';
import { dateToMMDDYYYY } from '../../utils/DateUtil';
import { DatePicker } from 'react-materialize';
import { makeStyles } from '@material-ui/core/styles';
import TextField from '@material-ui/core/TextField';

import './ProfileSignupDailyReport.css';

/**
 * from: https://v4.mui.com/components/pickers/
 */
const useStyles = makeStyles((theme) => ({
  container: {
    display: 'flex',
    flexWrap: 'wrap',
  },
  textField: {
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1),
    width: 200,
  },
}));

const ProfileSignupDailyReport = ({ auth }) => {
  const classes = useStyles();

  const PROVINCE_ARRAY = ['AB', 'BC', 'MB', 'SK'];

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [selectedDate, setSelectedDate] = useState(null);
  const [showResults, setShowResults] = useState(false);

  const { documents: profiles, loadFilteredDocuments, addToFilters, addRangeFilter } = useQueryServices('profiles', profileFields.CREATED_AT);

  const getQuery = (startDate, endDate) => {
    const SELECTED_DATE_TIMESTAMP = firebase.firestore.Timestamp.fromDate(startDate);
    const NEXT_DATE_TIMESTAMP = firebase.firestore.Timestamp.fromDate(endDate);

    addToFilters(profileFields.SIGN_UP_STATUS, '==', signUpStatus.SIGNED_UP);
    addToFilters(profileFields.COUNTRY_CODE, '==', countryCodes.CA);
    addToFilters(profileFields.STATE_CODE, 'in', PROVINCE_ARRAY);
    addRangeFilter(profileFields.CREATED_AT, '>=', SELECTED_DATE_TIMESTAMP, '<', NEXT_DATE_TIMESTAMP);
    loadFilteredDocuments(false);

    setShowResults(true);
  };

  const downloadXlsxReport = () => {
    setLoading(true);

    let sortedList = sortByState(generateProfileArray(profiles));

    const HEADINGS = ['Sign Up Date', 'Province/State', 'Country', 'Username', 'First Name', 'Last Name', 'Email Address', 'Age Group', 'Gender'];
    let newWorkbook = XLSX.utils.book_new();

    for (const province of PROVINCE_ARRAY) {
      let data = getArraybyRegion(sortedList, province);

      if (data.length === 0) {
        continue;
      }

      let sheet = XLSX.utils.json_to_sheet(data, { origin: 'A2', skipHeader: true });
      XLSX.utils.sheet_add_aoa(sheet, [HEADINGS]);

      XLSX.utils.book_append_sheet(newWorkbook, sheet, province);
    }

    let serializedWorkbook = XLSX.write(newWorkbook, {
      bookType: 'xlsx',
      bookSST: true,
      type: 'binary',
    });

    let downloadUrl = window.URL.createObjectURL(
      new Blob([serialFileToArrayBuffer(serializedWorkbook)], {
        type: 'application/octet-stream',
      }),
    );

    setLoading(false);

    let filename = `${dateToMMDDYYYY(selectedDate)}-Signup-Report.xlsx`;
    downloadFile(downloadUrl, filename);

    setMessage(`Downloaded Report for ${dateToMMDDYYYY(selectedDate)}`);
  };

  const handleDatePicker = (e) => {
    setLoading(true);
    let dateStr = e.target.value;

    let [yearStr, monthStr, dayStr] = dateStr.split('-');
    let year = parseInt(yearStr);
    let month = parseInt(monthStr);
    let day = parseInt(dayStr);

    let selectDate = new Date(year, --month, day);
    selectDate.setHours(0, 0, 0, 0);
    setSelectedDate(selectDate);

    let nextDayDate = new Date();
    nextDayDate.setDate(selectDate.getDate() + 1);
    nextDayDate.setHours(0, 0, 0, 0);

    getQuery(selectDate, nextDayDate);
    setLoading(false);
  };

  // copy from DownloadEntries.js
  const renderDatePicker = () => {
    return (
      <>
        <form className={classes.container} noValidate>
          <TextField
            id="datePicker"
            label="Pick a Date For Report"
            type="date"
            defaultValue="2021-12-01"
            className={classes.textField}
            InputLabelProps={{
              shrink: true,
            }}
            onChange={(e) => {
              handleDatePicker(e);
            }}
          />
        </form>
      </>
    );
  };

   if (!auth.uid) return <Redirect to="/signin" />;
  return (
    <div className="profile-signup-report-container">
      <h5>
        <b>Sign Up Daily Report</b>
      </h5>
      <p>Report only show data for following Canadian provinces: Alberta, British Columbia, Saskatchewan and Manitoba.</p>
      <div>
        {renderDatePicker()}
        {loading && <CircleLoader />}
        {showResults && !profiles.length && <p>{`No new user signed up: ${dateToMMDDYYYY(selectedDate)}`}</p>}
        {profiles.length > 0 && !loading && <StyledButton text="Download Report" onClickHandler={downloadXlsxReport} />}
        <p className="profile-signup-report-message">{message}</p>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => {
  return {
    auth: state.firebase.auth,
  };
};

export default compose(connect(mapStateToProps))(ProfileSignupDailyReport);
